import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Chip,
  Fade,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridRowModes,
} from "@mui/x-data-grid";
import CustomToolbar from "../../utils/customToolbar";
import { Delete, Edit, Save } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useApiClient } from "../../context/ApiClientContext";
import api from "../../helper/baseApi";
import SuccessAlert from "../../helper/successAlert";
import ErrorAlert from "../../helper/errorAlert";
import { Navigate, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import ConfirmDialog from "../../helper/confirmDialog";
import ComponentBackdrop from "../../components/backdrop";

const ValueTagList = ({ valueTagSet, teamID, refreshData }) => {

  const [rowData, setRowData] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const { apiUrls: { mobileApiUrl } } = useApiClient();
  const [iconOptions, setIconOptions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showButton, setShowButton] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const navigate = useNavigate();

  const handleOpenConfirm = () => {
    if (selectedIds.length === 0) {
      toast.warning("Please select at least one value tag to delete.");
      return;
    }
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => setOpenConfirm(false);

  const handleShowButton = (ids) => {
    setSelectedIds(ids);
    setShowButton(ids.length > 0);
  };
  
  const onSelectedCheckbox = (selectionModel, rows) => {
  let selectedIds = [];

  if (selectionModel.type === "include") {
    selectedIds = Array.from(selectionModel.ids).map((rowId) => {
      const row = rows.find((r) => r.id === rowId);
      return row?.teamValueTagId; // <-- send teamValueTagId
    });
  } else if (selectionModel.type === "exclude") {
    const excluded = new Set(selectionModel.ids);
    selectedIds = rows
      .filter((row) => !excluded.has(row.id))
      .map((row) => row.teamValueTagId); // <-- send teamValueTagId
  }
console.log(selectedIds);
  handleShowButton(selectedIds);
};

  function parseLineIconClasses(cssText) {
    const re = /\.lni-([a-z0-9-]+)\s*::?before/g;
    const names = new Set();
    let m;
    while ((m = re.exec(cssText)) !== null) {
      names.add(`lni-${m[1]}`);
    }
    return Array.from(names);
  }

  const validateRow = (row) => {
  const errors = {};

  if (!row.name || row.name.trim() === "") errors.name = true;
  if (!row.description || row.description.trim() === "") errors.description = true;
  if (!row.iconCode || row.iconCode.trim() === "") errors.iconCode = true;
  if (!row.passRate || row.passRate.toString().trim() === "") errors.passRate = true;

  return errors; // empty object means no errors
};


  useEffect(() => {
    const rows = (valueTagSet || []).map((row, index) => ({
      id: row.valueTagId || index,
      ...row,
    }));
    setRowData(rows);
  }, [valueTagSet]);

  // Load icon options
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/icons.css");
        const css = await res.text();
        const classes = parseLineIconClasses(css); // implement parseLineIconClasses
        const options = classes.map((c) => ({
          value: `lni ${c}`,
          label: c.replace("lni-", ""),
        }));
        setIconOptions(options);
      } catch (e) {
        console.error("Failed to load /icons.css", e);
      }
    })();
  }, []);

  // Enable editing by clicking Edit icon
  const handleEditClick = (id) => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.Edit },
    }));
  };

  const handleSaveClick = (id) => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View },
    }));
  };

  const saveRowToApi = async (updatedRow) => {
    try {
      setOpenBackdrop(true);
      const apiUrl = `${mobileApiUrl}team/update-team-vt`;
      const response = await api.post(apiUrl, {
        teamID,
        teamVTList: [
          {
            teamValueTagId: updatedRow.teamValueTagId,
            name: updatedRow.name,
            description: updatedRow.description,
            iconCode: updatedRow.iconCode,
            passRate: updatedRow.passRate,
            isActive: updatedRow.isActive ?? true,
          },
        ],
      });

      if (response.status === 200) {
        SuccessAlert(response.data.message);
      }

      // Update local rowData
      setRowData((prev) =>
        prev.map((r) => (r.id === updatedRow.id ? updatedRow : r))
      );
    } catch (error) {
      ErrorAlert(error, Navigate);
      return updatedRow; // fallback
    } finally {
      setOpenBackdrop(false);
    }
  };

  const processRowUpdate = async (updatedRow) => {
    const errors = validateRow(updatedRow);

  if (Object.keys(errors).length > 0) {
    // mark fields in the grid as errors
    updatedRow._errors = errors; // store errors in the row
    setRowData((prev) =>
      prev.map((r) => (r.id === updatedRow.id ? { ...updatedRow } : r))
    );
    toast.error("Please fill in all required fields.");
    throw new Error("Validation failed"); // prevent row from saving
  }

    if (updatedRow.isNew) {
      // create new
      const payload = {
        teamId: teamID,
        name: updatedRow.name,
        description: updatedRow.description,
        iconCode: updatedRow.iconCode,
        passRate: updatedRow.passRate,
      };

      const apiUrl = `${mobileApiUrl}team/add-team-vt`;
      const response = await api.post(apiUrl, payload);

      if (response.status === 200) {
        SuccessAlert(response.data.message);
        await refreshData();
      } 

      return { ...updatedRow, isNew: false };
    }

    // existing update logic for existing rows
    await saveRowToApi(updatedRow);
    return updatedRow;
  };

  const handleAddNewRecord = () => {
    const newId =
      rowData.length > 0 ? Math.max(...rowData.map(r => r.id)) + 1 : 1;

    const newRow = {
      id: newId,
      teamValueTagId: 0,
      name: "",
      description: "",
      iconCode: "",
      passRate: "",
      distributeRate: "",
      teamId: teamID,
      isNew: true
    };

    setRowData(prev => [...prev, newRow]);

    setRowModesModel(prev => ({
      ...prev,
      [newId]: { mode: GridRowModes.Edit, fieldToFocus: "name" }
    }));
  };

  const handleDelete = async () => {
    setOpenBackdrop(true);
    try {

      const apiUrl = `${mobileApiUrl}team/delete-team-vt`;
      const response = await api.post(apiUrl, { teamValueTagId: selectedIds });
      if (response.status === 200) {
        setTimeout(() => {
          setOpenBackdrop(false);

          const deletedItems = rowData.filter(item =>
            selectedIds.includes(item.teamValueTagId)
          );
          const deletedNames = deletedItems.map(item => item.name);

          toast.success(`Deleted: ${deletedNames.join(", ")}`);

          setRowData(prev =>
            prev.filter(item => !selectedIds.includes(item.teamValueTagId))
          );
          setSelectedIds([]);
          setShowButton(false);
          setOpenConfirm(false);
        }, 2000);

      } else {
        toast.error("Failed to delete team value tags. Please try again.");
      }
    } catch (error) {
      setTimeout(() => {
        ErrorAlert(error, navigate);
      }, 2000);
    } finally {
      setTimeout(() => {
        setOpenBackdrop(false);
        setSelectedIds([]);
        setShowButton(false);
      }, 2000);
    }
  };

  const columns = [
    {
      field: "Actions",
      headerName: "Action",
      width: 120,
      type: "actions",
      getActions: (params) => {
        const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <IconButton key="save" onClick={() => handleSaveClick(params.id)}>
              <Tooltip title="Save">
                <Save />
              </Tooltip>
            </IconButton>,
            <IconButton
              key="cancel"
              onClick={() => {
                const row = params.row;

                if (row.isNew) {
                  // ❗ If the row was newly added → delete it
                  setRowData((prev) => prev.filter((r) => r.id !== params.id));
                  return;
                }

                // ❗ Otherwise → normal cancel behavior
                setRowModesModel((prev) => ({
                  ...prev,
                  [params.id]: { mode: GridRowModes.View, ignoreModifications: true },
                }));
              }}
            >
              <Tooltip title="Cancel">
                <Delete />
              </Tooltip>
            </IconButton>,
          ];
        }
        return [
          <IconButton
            key="edit"
            onClick={() => handleEditClick(params.row.id)}
          >
            <Tooltip title="Edit">
              <Edit />
            </Tooltip>
          </IconButton>,
        ];
      },
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      editable: true,
      renderEditCell: (params) => (
        <TextField
          value={params.value || ""}
          error={params.row._errors?.name || false} // ❌ mark red if error
          helperText={params.row._errors?.name ? "Required" : ""}
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value,
            })
          }
          fullWidth
        />
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      editable: true,
      renderEditCell: (params) => (
        <TextField
          value={params.value || ""}
          error={params.row._errors?.description || false}
          helperText={params.row._errors?.description ? "Required" : ""}
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value,
            })
          }
          fullWidth
        />
      ),
    },
    {
      field: "iconCode",
      headerName: "Icon",
      width: 150,
      editable: true,
      renderCell: (params) => (
        <i className={params.value} style={{ fontSize: 20 }} />
      ),
      renderEditCell: (params) => (
        <TextField
          select
          fullWidth
          error={params.row._errors?.iconCode || false}
          helperText={params.row._errors?.iconCode ? "Required" : ""}
          value={params.value || ""}
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value,
            })
          }
        >
          {iconOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              <i className={opt.value} style={{ marginRight: 8 }} />
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      field: "passRate",
      headerName: "Pass Rate",
      width: 120,
      editable: true,
      renderEditCell: (params) => (
        <TextField
          type="number"
          value={params.value || ""}
          error={params.row._errors?.passRate || false}
          helperText={params.row._errors?.passRate ? "Required" : ""}
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value,
            })
          }
          fullWidth
        />
      ),
    },
    {
      field: "distributeRate",
      headerName: "Distribution Rate",
      width: 150,
      editable: false,
    },
  ];

  return (
    <Paper
      sx={{
        width: "100%",
        my: 3,
        py: 1,
        borderRadius: 2,
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)", // soft shadow
        border: "1px solid #e0e0e0", // subtle border
      }}
    >

      <ConfirmDialog
        open={openConfirm}
        onClose={handleCloseConfirm}
        onConfirm={handleDelete}
        title="Delete Team Members"
        message={`Are you sure you want to delete ${selectedIds.length > 1 ? "these team value tags" : "this team value tag"
          }?`}
        items={rowData
          .filter(item => selectedIds.includes(item.teamValueTagId))
          .map(item => item.name)}
        confirmText="Delete"
        confirmColor="error"
      />

      <DataGrid
        columns={columns}
        rows={rowData}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        processRowUpdate={processRowUpdate}
        showToolbar
        slots={{
          toolbar: CustomToolbar
        }}
        pageSizeOptions={[5, 10, { value: 50, label: "50" }]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
          sorting: { sortModel: [{ field: "passRate", sort: 'desc' }] }
        }}
        onRowSelectionModelChange={(id) => {
          onSelectedCheckbox(id, rowData);
        }
        }
        checkboxSelection
        disableRowSelectionOnClick
        localeText={{ noRowsLabel: "No records found" }}
        slotProps={{
          toolbar: {
            title: "Value Tag List",
            showAdd: true, // <-- show the Add button
            onAddClick: handleAddNewRecord,
            showDelete: showButton,
            onDelete: handleOpenConfirm,
          },
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "skeleton",
          },
          disableToolbarButton: {
            csvOptions: "true",
            printOptions: "true",
          },
        }}
        experimentalFeatures={{ newEditingApi: true }}
      />
      <ComponentBackdrop openBackdrop={openBackdrop} />

    </Paper>
    
  );
};

export default ValueTagList;